// routes/mobile/pwaUtils.js Shared PWA detection utilities

import { useCallback, useEffect, useRef, useState } from 'react'
import { IS_IOS } from '~/lib/constants'

const logger = console.scope('PWA Utils')

/**
 * Detect if running as installed PWA (standalone mode)
 */
export const isStandalone = () => {
	// iOS Safari
	if (window.navigator.standalone === true) return true
	// Android Chrome / other browsers
	if (window.matchMedia('(display-mode: standalone)').matches) return true
	if (window.matchMedia('(display-mode: fullscreen)').matches) return true
	return false
}

/**
 * Detect if on mobile device
 */
export const isMobile = () => {
	return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
}

/**
 * Detect iOS
 */
export const isIOS = () => {
	return /iPhone|iPad|iPod/i.test(navigator.userAgent)
}

const ua = navigator.userAgent

export const isIOSSafari = () => IS_IOS && /Safari/i.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/i.test(ua)

export const isChromeBrowser = () => /Chrome/i.test(ua) && !/Edge|OPR/i.test(ua)

// Store the deferred prompt globally so it persists across hook instances
let globalDeferredPrompt = null

/**
 * Hook to manage beforeinstallprompt event Returns canInstall state and triggerInstall function
 */
export const useInstallPrompt = () => {
	const [canInstall, setCanInstall] = useState(() => !!globalDeferredPrompt)
	const [installed, setInstalled] = useState(false)
	const isListening = useRef(false)

	useEffect(() => {
		if (isListening.current) return

		if (globalDeferredPrompt) {
			logger.info('Using existing deferred prompt')
			setCanInstall(true)
		}

		const handleBeforeInstall = e => {
			logger.info('beforeinstallprompt event received')
			e.preventDefault()
			globalDeferredPrompt = e
			setCanInstall(true)
		}

		const handleAppInstalled = () => {
			logger.info('appinstalled event received')
			setInstalled(true)
		}

		window.addEventListener('beforeinstallprompt', handleBeforeInstall)
		window.addEventListener('appinstalled', handleAppInstalled)
		isListening.current = true

		return () => {
			window.removeEventListener('beforeinstallprompt', handleBeforeInstall)
			window.removeEventListener('appinstalled', handleAppInstalled)
			isListening.current = false
		}
	}, [])

	const triggerInstall = useCallback(async () => {
		if (!globalDeferredPrompt) {
			logger.info('No deferred prompt available')
			return null
		}

		try {
			logger.info('Triggering install prompt')
			globalDeferredPrompt.prompt()
			const { outcome } = await globalDeferredPrompt.userChoice
			logger.info('User choice:', outcome)
			globalDeferredPrompt = null
			setCanInstall(false)
			return outcome
		} catch (err) {
			logger.error('Install prompt error:', err)
			globalDeferredPrompt = null
			setCanInstall(false)
			return null
		}
	}, [])

	return { canInstall, installed, triggerInstall }
}
